import i18n from '@/i18n'
import isNotNil from '@/lib/utils/isNotNil'
import { ChunkFlushArgs } from '@/modules/aiChat/aiConnection/buffer'
import { EventsHandlerFactory, EventType, UTMParams } from '@/modules/aiChat/aiConnection/types'
import {
  AIGeneratedField,
  AIGeneratedForm,
  ConversationMessageMarks,
  GeneratedFormArtifactType,
  IdentifiedScene,
  supportedScenes,
} from '@/redux/slices/formAIGenerationSlice'
import { ArtifactTypeEnum, FieldClass, Scene } from '@/typings/domain'
import { nanoid } from '@reduxjs/toolkit'
import { cloneDeep, max } from 'lodash-es'
import fixAIGeneratedField from './fixAIGeneratedField'
import headerImageSuggestionsForUntitledForm from './headerImageSuggestionsForUntitledForm.json'
import isValidAIGeneratedField from './isValidAIGeneratedField'
import {
  AddFormFieldEvent,
  AddFormFieldFinishedEvent,
  AddFormFieldPropertyDeltaEvent,
  AddFormFieldStartedEvent,
  DeleteAllFieldRulesEvent,
  DeleteFormFieldsEvent,
  FormCustomEvents,
  FormGenerationClientCommand,
  FormGenerationToolCallEvents,
  FormGenerationToolNames,
  FormStepEvents,
  GenerateFieldRulesOutlineEvent,
  JiriStepEvent,
  MoveFormFieldEvent,
  UpdateFieldRuleChunkEvent,
  UpdateFieldRulesEvent,
  UpdateFormFieldEvent,
  UpdateFormFieldsPropertiesEvent,
  UpdateFormHeaderImageEvent,
  UpdateFormSettingEvent,
} from './types'

export type Options = {
  conversationId: string
  getCurrentMessageId: () => string
  getLatestGeneratedForm: () => AIGeneratedForm | undefined
  upsertGeneratedForm: (form: AIGeneratedForm) => void
  setSelectedFormId: (id: string) => void
  setConversationMessageMarks: (marks: ConversationMessageMarks) => void
  removeGeneratedForm: (formId: string) => void
  reportGeneratedForm: (form: AIGeneratedForm) => void
  onStopped: (interrupted?: true) => void
}

export default function createFormGenerationEventsHandlerFactory({
  conversationId,
  getCurrentMessageId,
  getLatestGeneratedForm,
  upsertGeneratedForm,
  setSelectedFormId,
  setConversationMessageMarks,
  removeGeneratedForm,
  reportGeneratedForm,
  onStopped,
}: Options): EventsHandlerFactory<FormStepEvents, FormCustomEvents, FormGenerationClientCommand> {
  return processor => {
    let generatingFormState: {
      form: AIGeneratedForm
      associatedToMessage?: boolean
      /** Record<field_index, {}> */
      streamingFieldStates: Record<number, { id: string; finished: boolean }>
      /** Record<field_id, field_index> */
      addFieldIndexesMap: Record<string, number>
      addFieldUniqueFieldIndexes: Set<number>
    } | null = null
    let currentGeneratedForm: AIGeneratedForm | undefined = undefined
    let identifiedScene: IdentifiedScene | undefined = undefined

    function draftNewGeneratingForm(withCurrentGeneratedForm: boolean, artifactId: string) {
      currentGeneratedForm = getLatestGeneratedForm()
      generatingFormState = {
        form: {
          conversationId,
          name: '',
          description: '',
          ...(withCurrentGeneratedForm ? currentGeneratedForm : {}),
          scene:
            (identifiedScene === 'unknown' ? undefined : identifiedScene) ||
            (withCurrentGeneratedForm ? currentGeneratedForm?.scene : undefined),
          fields: [...(withCurrentGeneratedForm ? (currentGeneratedForm?.fields ?? []) : [])],
          id: artifactId,
          done: false,
          isNotModifiedDuringMessage: true,
          messageId: undefined,
          updatingState: {
            createdByUpdate: !!withCurrentGeneratedForm,
            oldValues: withCurrentGeneratedForm
              ? {
                  name: currentGeneratedForm?.name ?? '',
                  description: currentGeneratedForm?.description ?? '',
                }
              : undefined,
            changingProperties: [],
          },
        },
        streamingFieldStates: {},
        addFieldIndexesMap: {},
        addFieldUniqueFieldIndexes: new Set(),
      }
    }

    function cleanupFormStates() {
      generatingFormState = null
    }

    function wrapUpdateGeneratingForm<D>(
      updateFn: (state: NonNullable<typeof generatingFormState>, data: D) => void | boolean,
      isRealUpdateOperation = true
    ) {
      return (data: D) => {
        if (!generatingFormState) return

        if (updateFn(generatingFormState, data) === false) return

        if (isRealUpdateOperation) {
          generatingFormState.form.isNotModifiedDuringMessage = false
          if (!generatingFormState.form.messageId) generatingFormState.form.messageId = getCurrentMessageId()
        }
        upsertGeneratedForm(generatingFormState.form)

        if (!generatingFormState.associatedToMessage) {
          generatingFormState.associatedToMessage = true
          processor.upsertMessage?.({
            chunks: [
              {
                key: generatingFormState.form.id,
                type: 'artifact',
                artifactType: ArtifactTypeEnum.Form,
                artifactId: generatingFormState.form.id,
              },
            ],
          })

          setSelectedFormId(generatingFormState.form.id)
        }
      }
    }

    const handleReportGeneratingFormChunk = wrapUpdateGeneratingForm(
      ({ form }, { key, chunk }: { key: string; chunk: string }) => {
        if (key === 'form_name') form.name += chunk
        if (key === 'form_description') form.description += chunk
      }
    )

    const handleUpdateFormNameDescription = wrapUpdateGeneratingForm<{ name?: string; description?: string }>(
      ({ form }, { name, description }) => {
        if (name) form.name = name
        if (description) form.description = description
      }
    )

    const handleUpdateFormSetting = wrapUpdateGeneratingForm(
      ({ form }, { value: { content } }: UpdateFormSettingEvent) => {
        form.setting = { ...form.setting, ...content }
      }
    )

    const handleUpdateFormHeaderImage = wrapUpdateGeneratingForm(
      ({ form }, { value: { content: images } }: UpdateFormHeaderImageEvent) => {
        const headerImageUrls: string[] = []

        for (let image of images ?? []) {
          if (!image) continue

          const matched = /(https:\/\/[A-Za-z0-9-._?&=%+!*/]+)/.exec(image) // maybe markdown syntax
          if (matched) image = matched[1] as string
          try {
            headerImageUrls.push(new URL(image).toString())
          } catch {
            continue
          }
        }

        if (!headerImageUrls.length) return false

        form.alternativeHeaderImageUrls = headerImageUrls
        form.theme = { headerImageUrl: headerImageUrls[0]! }
      }
    )

    const handleAddFormField = wrapUpdateGeneratingForm(
      (
        { form, addFieldIndexesMap, addFieldUniqueFieldIndexes },
        { value: { content: field, metadata, index } }: AddFormFieldEvent
      ) => {
        if (typeof index === 'number' && addFieldUniqueFieldIndexes.has(index)) return false

        const fieldWithId: NonNullable<AIGeneratedForm['fields']>[number] = {
          ...fixAIGeneratedField(field),
          _id: nanoid(),
        }
        if (!isValidAIGeneratedField(fieldWithId)) return false

        if (!fieldWithId.apiCode || form.fields?.some(existingField => existingField.apiCode === fieldWithId.apiCode)) {
          const maxFieldApiCodeSeq =
            max(form.fields?.map(field => +(field.apiCode?.substring('field_'.length) ?? '0') || 0)) || 0
          fieldWithId.apiCode = `field_${maxFieldApiCodeSeq + 1}`
        }

        let insertAt = form.fields?.length ?? 0
        if (typeof metadata?.position === 'number' && metadata.position >= 1) {
          insertAt = metadata.position - 1
        }
        form.fields = [...(form.fields ?? []).slice(0, insertAt), fieldWithId, ...(form.fields ?? []).slice(insertAt)]
        if (typeof index === 'number') {
          addFieldIndexesMap[fieldWithId._id] = index
          sortFieldsByIndexesMap(form.fields, addFieldIndexesMap)
          addFieldUniqueFieldIndexes.add(index)
        }
      }
    )

    const handleAddFormFieldStreaming = wrapUpdateGeneratingForm(
      (
        { form, streamingFieldStates },
        data: AddFormFieldStartedEvent | AddFormFieldPropertyDeltaEvent | AddFormFieldFinishedEvent
      ) => {
        if (data.name === 'add_field_started') {
          if (streamingFieldStates[data.value.field_index]) return false

          const newField: NonNullable<AIGeneratedForm['fields']>[number] = fixAIGeneratedField({
            _id: nanoid(),
            type: data.value.metadata?.field_type as FieldClass,
            customizedType: data.value.metadata?.field_customized_type,
            label: '',
          })

          streamingFieldStates[data.value.field_index] = { id: newField._id, finished: false }
          form.fields = [...(form.fields ?? []), { ...newField }]
          return
        }

        if (data.name === 'add_field_property_delta') {
          processor.reportChunk({
            key: `form_field_${data.value.metadata.field_index}_${data.value.property}`,
            chunk: data.value.delta,
            index: data.value.metadata.index,
          })
          return false
        }
        if (data.name === 'add_field_remove_duplicated') {
          const streamingFieldState = streamingFieldStates[data.value.field_index]
          if (!streamingFieldState) return false

          form.fields = form.fields?.filter(field => field._id !== streamingFieldState.id)

          delete streamingFieldStates[data.value.field_index]
        }

        if (data.name === 'add_field_finished') {
          const streamingFieldState = streamingFieldStates[data.value.field_index]
          if (!streamingFieldState) return false

          const completeField = fixAIGeneratedField(data.value.content)
          const formFieldIndex = form.fields?.findIndex(field => field._id === streamingFieldState.id) ?? -1
          if (formFieldIndex === -1) return false

          const currentField = { ...form.fields![formFieldIndex]!, ...completeField }

          currentField._id = nanoid()
          streamingFieldStates[data.value.field_index] = { id: currentField._id, finished: true }
          form.fields![formFieldIndex] = currentField

          const fieldsIndexMap = Object.fromEntries(
            Object.entries(streamingFieldStates).map(([fieldIndex, state]) => [state.id, +fieldIndex])
          )

          if (!isValidAIGeneratedField(currentField)) {
            form.fields = form.fields?.filter(field => field._id !== currentField._id)
          }

          form.fields = sortFieldsByIndexesMap([...(form.fields ?? [])], fieldsIndexMap).map((field, index) => ({
            ...field,
            apiCode: `field_${index + 1}`,
          }))
        }
      }
    )

    const handleAddFormFieldPropertyDeltaChunkFlush = wrapUpdateGeneratingForm(
      ({ form, streamingFieldStates }, { key, chunk }: { key: string; chunk: string }) => {
        const [fieldIndexStr, property, choiceIndexStr] = key.substring('form_field_'.length).split('_') as
          | [string, 'label' | 'notes' | 'explanation', string | undefined]
          | [string, 'choice', string]

        const fieldIndex = +fieldIndexStr
        const newFieldState = streamingFieldStates[fieldIndex]
        if (!newFieldState || newFieldState.finished) return false

        const formFieldIndex = form.fields?.findIndex(field => field._id === newFieldState.id) ?? -1
        if (formFieldIndex === -1) return false

        const newField = { ...form.fields![formFieldIndex]! }

        if (property === 'label') newField.label += chunk
        if (property === 'notes') newField.notes = (newField.notes ?? '') + chunk
        if (property === 'explanation')
          newField.examSetting = {
            ...newField.examSetting,
            score: newField.examSetting?.score ?? null,
            answer: newField.examSetting?.answer ?? null,
            explanation: (newField.examSetting?.explanation ?? '') + chunk,
          }
        if (property === 'choice') {
          const choiceIndex = +choiceIndexStr
          newField.choices = [...(newField.choices ?? [])]
          const choice = newField.choices[choiceIndex]
          if (choice) {
            newField.choices[choiceIndex] = { ...choice, name: choice.name + chunk }
          } else {
            newField.choices[choiceIndex] = { name: chunk }
          }
          for (let i = 0; i < newField.choices.length; i++) {
            if (!newField.choices[i]) newField.choices[i] = { name: '' }
          }
        }

        newField._id = nanoid()
        streamingFieldStates[fieldIndex]!.id = newField._id
        form.fields![formFieldIndex] = newField
      }
    )

    const handleDeleteFormFields = wrapUpdateGeneratingForm(
      ({ form }, { value: { content: apiCodes } }: DeleteFormFieldsEvent) => {
        if (!apiCodes) return false
        form.fields = form.fields?.filter(
          field => !apiCodes.includes(field.apiCode ?? '') && !apiCodes.includes(field._id)
        )
      }
    )

    const handleUpdateFormField = wrapUpdateGeneratingForm(
      ({ form }, { value: { content, metadata } }: UpdateFormFieldEvent) => {
        const fieldIndex =
          form.fields?.findIndex(
            field => field.apiCode === metadata.reference_api_code || field._id === metadata.reference_api_code
          ) ?? -1
        if (fieldIndex === -1) return false

        let newField: AIGeneratedField = { ...form.fields![fieldIndex]! }
        if (!isValidAIGeneratedField(newField)) return false

        const updateContentKeys = Object.keys(content) as (keyof typeof content)[]
        updateContentKeys
          .filter(key => isNotNil(content[key]))
          .forEach(key => {
            ;(newField as any)[key] = content[key]
          })

        newField = fixAIGeneratedField(newField)
        if (!isValidAIGeneratedField(newField)) return false

        form.fields![fieldIndex] = { ...newField, _id: nanoid() }
      }
    )

    const handleUpdateFormFieldsProperties = wrapUpdateGeneratingForm(
      ({ form }, { value: { content, metadata } }: UpdateFormFieldsPropertiesEvent) => {
        if (!form.fields) return false

        const updatedApiCodes = new Set(metadata.reference_api_codes)

        form.fields = form.fields.map(field => {
          if (!field.apiCode || !updatedApiCodes.has(field.apiCode)) return field
          const newField = { ...field }
          if (isNotNil(content.label)) newField.label = content.label
          if ('notes' in content) newField.notes = content.notes
          if (isNotNil(content.required)) newField.required = content.required
          if (isNotNil(content.score)) newField.examSetting = { ...newField.examSetting, score: content.score }
          if (isNotNil(content.answer)) newField.examSetting = { ...newField.examSetting, answer: content.answer }
          if ('explanation' in content) {
            newField.examSetting = { ...newField.examSetting, explanation: content.explanation }
          }
          if (content.choices_properties && Array.isArray(newField.choices)) {
            newField.choices = newField.choices.map((choice, index) => {
              const choiceProperty = content.choices_properties?.find(
                prop => prop.index === index || prop.value === choice.value || prop.name === choice.name
              )
              if (choiceProperty && isNotNil(choiceProperty.score)) {
                return { ...choice, score: choiceProperty.score }
              }
              return choice
            })
          }
          newField._id = nanoid()
          return newField
        })
      }
    )

    const handleMoveFormField = wrapUpdateGeneratingForm(
      ({ form }, { value: { content, metadata } }: MoveFormFieldEvent) => {
        if (!form.fields) return false

        const newFields = [...(form.fields ?? [])]

        const oldIndex = newFields.findIndex(
          field => field.apiCode === content.apiCode || field._id === content.apiCode
        )
        if (oldIndex === -1) return false

        const targetField = newFields.splice(oldIndex, 1)[0]!

        const newIndex = metadata.position - 1
        if (newIndex < 0) return false

        newFields.splice(newIndex, 0, targetField)

        form.fields = newFields
      }
    )

    const handleUpdateFieldRules = wrapUpdateGeneratingForm(
      (
        { form },
        data:
          | GenerateFieldRulesOutlineEvent
          | UpdateFieldRuleChunkEvent
          | UpdateFieldRulesEvent
          | DeleteAllFieldRulesEvent
      ) => {
        if (data.name === 'generate_field_rules_outline') {
          form.fieldRulesOutline = data.value.content
        }
        if (data.name === 'update_field_rules') {
          form.fieldRules = data.value.content
        }
        if (data.name === 'delete_all_field_rules') {
          form.fieldRulesOutline = null
          form.fieldRules = null
        }
      }
    )

    function handleJiriCompleted(data: JiriStepEvent & { type: EventType.StepFinished }) {
      const rawEvent = data.raw_event
      const scene = rawEvent.scene
      identifiedScene = scene ?? undefined
      if (scene && scene !== 'unknown' && !supportedScenes.includes(scene)) {
        setConversationMessageMarks({ identifiedUnsupportedScene: scene })
      }
    }

    function handleFormGenerationToolCallEvents({ type, tool_call_name }: FormGenerationToolCallEvents) {
      const form = generatingFormState?.form
      if (!form) return

      const changingProperty = toolNameToChangeProperty(tool_call_name)
      if (!changingProperty) return

      wrapUpdateGeneratingForm(({ form }) => {
        if (type === EventType.ToolCallStart) {
          form.updatingState.changingProperties = [...form.updatingState.changingProperties, changingProperty]
        }
        if (type === EventType.ToolCallEnd) {
          form.updatingState.changingProperties = form.updatingState.changingProperties.filter(
            property => property !== changingProperty
          )
        }

        if (type === EventType.ToolCallStart) {
          if (changingProperty === 'name') form.name = ''
          if (changingProperty === 'description') form.description = ''
        }
      }, false)(null)

      if (type === EventType.ToolCallEnd) {
        if (changingProperty === 'name') processor.reportChunk({ key: 'form_name', done: true })
        if (changingProperty === 'description') processor.reportChunk({ key: 'form_description', done: true })
        if (changingProperty === 'fields') reportGeneratedFormSnapshot()
      }
    }

    function reportGeneratedFormSnapshot() {
      if (!generatingFormState) return

      processor.reportChunk({ flush: true })
      reportGeneratedForm(generatingFormState.form)
    }

    async function handleFormGenerationFinished(interrupted?: true) {
      if (!generatingFormState) return

      processor.reportChunk({ flush: true })

      if (generatingFormState.form.isNotModifiedDuringMessage) {
        removeGeneratedForm(generatingFormState.form.id)
        if (currentGeneratedForm) setSelectedFormId(currentGeneratedForm.id)

        cleanupFormStates()
        return
      }

      generatingFormState.form.done = true
      generatingFormState.form.updatingState.changingProperties = []
      if (typeof interrupted === 'boolean') generatingFormState.form.interrupted = interrupted

      const form = generatingFormState.form
      await fillEmptyFormIfNeeded(form)
      upsertGeneratedForm(form)
      currentGeneratedForm = cloneDeep(form)
      reportGeneratedForm(currentGeneratedForm)

      cleanupFormStates()
    }

    function handleChunkFlush({ key, chunk }: ChunkFlushArgs) {
      if (key === 'form_name' || key === 'form_description') {
        handleReportGeneratingFormChunk({ key, chunk })
        return true
      }
      if (key.startsWith('form_field_')) {
        handleAddFormFieldPropertyDeltaChunkFlush({ key, chunk })
        return true
      }
    }

    const handleExtraStepEvents = (data: FormStepEvents) => {
      if (data.type === EventType.ToolCallStart || data.type === EventType.ToolCallEnd) {
        return handleFormGenerationToolCallEvents(data)
      }

      switch (data.step_name) {
        case 'jiri':
          if (data.type === EventType.StepFinished) handleJiriCompleted(data)
          return
        case 'create_form':
        case 'update_form':
        case 'duplicate_form':
        case 'create_exam_from_subject':
        case 'create_evaluation_from_subject':
          if (data.type === EventType.StepStarted) {
            draftNewGeneratingForm(data.step_name === 'update_form', data.raw_event.artifact_id)
          }
          return
      }
    }

    const handleExtraCustomEvents = (data: FormCustomEvents) => {
      switch (data.name) {
        case 'update_form_name':
          processor.reportChunk({ key: 'form_name', flush: true })
          return handleUpdateFormNameDescription({ name: data.value.content })
        case 'update_form_name_chunk':
          return processor.reportChunk({ key: 'form_name', chunk: data.value.delta, index: data.value.index ?? 1 })
        case 'update_form_description':
          processor.reportChunk({ key: 'form_description', flush: true })
          return handleUpdateFormNameDescription({ description: data.value.content })
        case 'update_form_description_chunk':
          return processor.reportChunk({
            key: 'form_description',
            chunk: data.value.delta,
            index: data.value.index ?? 1,
          })
        case 'update_form_header_image':
          return handleUpdateFormHeaderImage(data)
        case 'add_field':
          return handleAddFormField(data)
        case 'add_field_started':
        case 'add_field_property_delta':
        case 'add_field_finished':
        case 'add_field_remove_duplicated':
          return handleAddFormFieldStreaming(data)
        case 'update_field':
          return handleUpdateFormField(data)
        case 'update_fields_properties':
          return handleUpdateFormFieldsProperties(data)
        case 'move_field':
          return handleMoveFormField(data)
        case 'delete_fields':
          return handleDeleteFormFields(data)
        case 'generate_field_rules_outline':
        case 'update_field_rules':
        case 'delete_all_field_rules':
          return handleUpdateFieldRules(data)
        case 'update_form_setting':
          return handleUpdateFormSetting(data)
      }
    }

    const handleStopped = async (interrupted?: true) => {
      onStopped?.(interrupted)

      if (generatingFormState) {
        await handleFormGenerationFinished(interrupted)
      }
    }

    return {
      onChunkFlush: handleChunkFlush,
      onStopped: handleStopped,
      onCheckRiskRejected: () => {
        if (generatingFormState) removeGeneratedForm(generatingFormState.form.id)
        cleanupFormStates()
      },
      onExtraStepEvents: handleExtraStepEvents,
      onExtraCustomEvents: handleExtraCustomEvents,
    }
  }
}

async function fillEmptyFormIfNeeded(form: AIGeneratedForm) {
  if (!form.name) {
    try {
      await i18n.loadNamespaces(`scenes/${form.scene ?? Scene.Form}`)
      form.name = i18n.t('newForm.name', { ns: `scenes/${form.scene ?? Scene.Form}` })
    } catch {
      // ignored
    }
  }
  if (!form.theme?.headerImageUrl) {
    const randomImage =
      headerImageSuggestionsForUntitledForm[Math.floor(Math.random() * headerImageSuggestionsForUntitledForm.length)]!
    form.theme = { headerImageUrl: `${process.env.EXPO_PUBLIC_STATIC_ASSETS_DOMAIN}${randomImage.path}` }
  }
}

function sortFieldsByIndexesMap(fields: NonNullable<AIGeneratedForm['fields']>, indexesMap: Record<string, number>) {
  return fields.sort((a, b) => {
    const aIndex = indexesMap[a._id]
    const bIndex = indexesMap[b._id]
    if (typeof aIndex === 'number' && typeof bIndex === 'number') return aIndex - bIndex
    return 0
  })
}

const toolNameToChangeProperty = (
  toolName: FormGenerationToolNames | undefined
): NonNullable<AIGeneratedForm['updatingState']>['changingProperties'][number] | undefined => {
  // A ToolCallEnd whose ToolCallStart was never seen reaches here without a tool_call_name
  if (!toolName) return undefined
  if (toolName.endsWith('_form_header_image')) return 'headerImage'
  if (toolName.endsWith('_form_name')) return 'name'
  if (toolName.endsWith('_form_description')) return 'description'
  if (toolName.endsWith('_form_fields')) return 'fields'
  if (toolName === 'generate_raw_exam_content_from_subject') return 'fields'
  if (toolName === 'add_more_exam_fields') return 'fields'
  if (toolName === 'group_raw_form_fields_content') return 'fields'
  if (toolName.endsWith('_field_rules')) return 'fieldRules'
}

export function buildFormArtifact(form: AIGeneratedForm): GeneratedFormArtifactType {
  return {
    scene: form.scene,
    theme: form.theme
      ? { headerImageId: form.theme.headerImageId, headerImageUrl: form.theme.headerImageUrl }
      : undefined,
    name: form.name ?? '',
    description: form.description ?? '',
    fields: form.fields ?? [],
    field_rules: form.fieldRules,
    field_rules_outline: form.fieldRulesOutline,
    alternative_header_image_urls: form.alternativeHeaderImageUrls,
    setting: form.setting,
  }
}

export const FORM_GENERATION_UTM_PARAMS: UTMParams = { device: 'mobile', source: 'nextapp' }
const MESSAGE_EXTRA_PARAMS = { gd_version: 'next' }
export const FORM_GENERATION_EXTRA_MESSAGE_PARAMS = { ...MESSAGE_EXTRA_PARAMS, form_action: 'generate_form' }

export const PERFORM_MESSAGE_EXTRA_PARAMS = {
  ...MESSAGE_EXTRA_PARAMS,
  utm_device: FORM_GENERATION_UTM_PARAMS.device,
  utm_source: FORM_GENERATION_UTM_PARAMS.source,
  utm_medium: FORM_GENERATION_UTM_PARAMS.medium,
}
